import classNames from "classnames"
import { storyblokEditable } from "gatsby-source-storyblok"
import React, { useEffect, useState } from "react"

import CountDownBanner from "./CountDownBanner"
import IconBanner from "./IconBanner"
import Section from "../../..//elements/V2/Section"
import { Color } from "../../../../constants/V2/color"
import LayoutItem from "../../Layouts/LayoutItem"

import CloseIcon from "@components/elements/V2/Icons/CloseIcon"
import { trackSegmentEvent } from "@utils/analytics"
import { usePageLayoutState } from "@utils/layout"
import { getBackgroundColorClass } from "@utils/V2/color"

export type ToastNotificationsBaseProps = {
  ctaText: string
  ctaLink: string
  ctaColor?: Color
  textColor?: Color
  backgroundColor: Color
  mobileGridOrder?: number
  isSiteWideBanner?: boolean
  onClick?: VoidFunction
}

export type ToastNotificationsCountDownProps = {
  text?: string
  timerLabel: string
  ctaTextColor?: Color
  timerTextColor?: Color
  timerTargetDate: string
  component: "toastNotificationCountDownBanner"
}

export type ToastNotificationsIconProps = {
  icon?: string
  iconAlt?: string
  text: string
  mobileText?: string
  component: "toastNotificationIconBanner"
}

type Props = ToastNotificationsBaseProps &
  (ToastNotificationsCountDownProps | ToastNotificationsIconProps) &
  ReturnType<typeof storyblokEditable>

const ToastNotification = ({
  text,
  ctaText,
  ctaLink,
  ctaColor,
  textColor,
  backgroundColor,
  mobileGridOrder,
  isSiteWideBanner = false,
  ...rest
}: Props) => {
  const [isClosed, setIsClosed] = useState(false)

  const { setHasSiteBanner: setSiteBanner } = usePageLayoutState()

  useEffect(() => {
    if (window) {
      setSiteBanner(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const storyblokProps = {
    "data-blok-c": rest["data-blok-c"],
    "data-blok-uid": rest["data-blok-uid"],
  }

  const siteWideBannerTrackingEvent = isSiteWideBanner
    ? () => {
        trackSegmentEvent("site_wide_banner_click", {
          anchorText: ctaText,
          bannerText: text,
          path: ctaLink,
        })
      }
    : undefined

  return (
    <LayoutItem
      as={Section}
      mobileGridOrder={mobileGridOrder}
      {...storyblokProps}
      className={classNames(
        "flex h-[50px] items-center justify-between gap-10",
        getBackgroundColorClass(backgroundColor),
        { hidden: isClosed }
      )}
    >
      {rest.component === "toastNotificationCountDownBanner" ? (
        <CountDownBanner
          text={text}
          ctaText={ctaText}
          ctaLink={ctaLink}
          ctaColor={ctaColor}
          textColor={textColor}
          timerLabel={rest.timerLabel}
          backgroundColor={backgroundColor}
          timerTextColor={rest.timerTextColor}
          timerTargetDate={rest.timerTargetDate}
          onClick={siteWideBannerTrackingEvent}
        />
      ) : (
        <IconBanner
          text={text!}
          mobileText={rest.mobileText}
          icon={rest.icon}
          ctaText={ctaText}
          ctaLink={ctaLink}
          ctaColor={ctaColor}
          textColor={textColor}
          iconAlt={rest.iconAlt}
          backgroundColor={backgroundColor}
          onClick={siteWideBannerTrackingEvent}
        />
      )}
      <div className="h-24 w-24 cursor-pointer">
        <CloseIcon
          width="24px"
          height="24px"
          hideBorder
          strokeWidth="2"
          strokeColor={textColor}
          onClick={() => {
            setIsClosed(true)
            setSiteBanner(false)
          }}
        />
      </div>
    </LayoutItem>
  )
}

export default ToastNotification
